import {
    CaosCommand,
    CaosDefinitions,
    CommandGroups,
    GameVariant,
    int,
    Nullable,
    ValuesList,
    VariantData,
    VariantDataMap
} from "./caos-def";
import _caosDefinitions from "./caos-doc";
import {isInt} from "./utils";


export type CaosLib = {

    getCommands(): CaosCommand[];

    /**
     * Gets a command using its name, its map key or its id
     * @param idOrName map name, id or name
     */
    getCommand(idOrName: number | string): Nullable<CaosCommand>;

    /**
     * Gets values lists as a simple array
     */
    getValuesLists(): ValuesList[];

    /**
     * Gets a value list using its name, its map key or its id
     * @param idOrName map name, id or name
     */
    getValuesList(idOrName: number | string): Nullable<ValuesList>;

    /**
     * Gets a command group name from its ID
     * @param id
     */
    getCommandGroup(id: number): Nullable<string>;

    /**
     * Gets variant map data in  Game variant keyed object
     */
    getVariantMap(): VariantDataMap;

    /**
     * Gets the variant data for a given variant
     */
    getVariantData(variant: GameVariant): VariantData

    /**
     * Gets the variable type from its DB id;
     * Returns a screen-reader friendly string if friendly is true
     * @param typeId
     * @param friendly TRUE to return a screen-reader friendly string
     */
    getVariableType(typeId: int, friendly?: boolean): Nullable<string>;
}

let _caosLib: Nullable<CaosLib> = null;

export function getCaosLib(): CaosLib {
    if (_caosLib != null) {
        return _caosLib;
    }
    const definitions: CaosDefinitions = _caosDefinitions;
    return _caosLib = new CaosLibObject(definitions);
}

class CaosLibObject implements CaosLib {

    private _commands: Nullable<CaosCommand[]> = null;

    private _valuesLists: Nullable<ValuesList[]> = null;

    private _commandIdsByMixedKeysMap: Nullable<{ [id: string | int]: string }>

    private _valuesListIdsByMixedKeysMap: Nullable<{ [id: string | int]: string }>

    private _whitespaceRegex = /\s+/g;

    constructor(private _caosDefinitions: CaosDefinitions) {

    }

    /**
     * Gets commands as a simple array
     */
    getCommands(): CaosCommand[] {
        if (this._commands != null) {
            return this._commands;
        }
        return this._commands = Object.values(this._caosDefinitions.commands);
    }

    /**
     * Gets a command using either its id, name or original JSON key
     * @param idOrName
     */
    getCommand(idOrName: string | int): Nullable<CaosCommand> {
        // get the map of possible reference keys to commands object keys
        const map = this._getCommandsMap();
        return this._getForKey(
            map,
            this._caosDefinitions.commands,
            idOrName
        )
    }


    /**
     * Gets values lists as a simple array
     */
    getValuesLists(): ValuesList[] {
        if (this._valuesLists != null) {
            return this._valuesLists;
        }
        const out = Object.values(this._caosDefinitions.values_lists);
        this._valuesLists = out;
        return out;
    }

    /**
     * Gets a command using either its id, name or original JSON key
     * @param idOrName
     */
    getValuesList(idOrName: string | number): Nullable<ValuesList> {
        const map = this._getValuesListMap();
        return this._getForKey(
            map,
            this._caosDefinitions.values_lists,
            idOrName
        )
    }

    /**
     * Gets the variant data map
     */
    getVariantMap(): VariantDataMap {
        return this._caosDefinitions.variant_map;
    }

    /**
     * Get variant data object for a given variant
     * @param variant
     */
    getVariantData(variant: GameVariant): VariantData {
        return this._caosDefinitions.variant_map[variant];
    }

    /**
     * Gets an associative array of command groups by id
     */
    getCommandGroups(): CommandGroups {
        return this._caosDefinitions.command_groups;
    }

    /**
     * Gets a command group's name by its id
     * @param id
     */
    getCommandGroup(id: number): Nullable<string> {
        const commandGroups = this.getCommandGroups();
        if (typeof commandGroups[id] === "undefined") {
            return null;
        }
        return commandGroups[id];
    }


    getVariableType(typeId: number, friendly?: boolean): Nullable<string> {
        const variableTypes = this._caosDefinitions.variable_types;
        if (typeof variableTypes[typeId] === "undefined") {
            return null;
        }
        const type = variableTypes[typeId];
        if (friendly !== true) {
            return type;
        }
        switch (type.toLowerCase()) {
            case "[anim]":
                return "animation";
            case "[string]":
                return "string";
            default:
                return type;
        }
    }

    /**
     * Uses a key
     * @param keyMap
     * @param valuesMap
     * @param idOrName
     * @private
     */
    private _getForKey<T>(
        keyMap: { [id: string | int]: string },
        valuesMap: { [id: string | int]: T },
        idOrName: string | int,
    ): Nullable<T> {

        //Normalize name for search if is string
        if (typeof idOrName === "string") {
            idOrName = this._crushName(idOrName);
        }

        // If key does not exist; return null;
        if (typeof keyMap[idOrName] === "undefined") {
            return null;
        }

        // Get original key from commands JSON object
        const originalKey = keyMap[idOrName];

        // Return the object for key
        return valuesMap[originalKey];
    }


    /**
     * Creates a map file which maps all possible keys to their associated command.
     * Map uses both actual key, id, name, and key as number if needed
     * @private
     */
    private _getCommandsMap() {
        if (this._commandIdsByMixedKeysMap) {
            return this._commandIdsByMixedKeysMap;
        }

        // Get commands to process
        const commands: { [id: string]: CaosCommand } = this._caosDefinitions.commands;


        // Create output map
        const commandsByKey: { [key: int | string]: string } = {};


        // Create all possible keys to find a command by
        for (const key in commands) {
            const command = commands[key];
            commandsByKey[key] = key;
            commandsByKey[this._crushName(command.command)] = key;
            if (isInt(key)) {
                commandsByKey[parseInt(key, 10)] = key;
            }
            commandsByKey[command.id] = key;
        }
        this._commandIdsByMixedKeysMap = commandsByKey;
        return commandsByKey;
    }

    /**
     * Creates a map file which maps all possible keys to their associated values list.
     * Map uses both actual key, id, name, and key as number if needed
     * @private
     */
    private _getValuesListMap() {
        if (this._valuesListIdsByMixedKeysMap) {
            return this._valuesListIdsByMixedKeysMap;
        }

        // Get commands to process
        const valuesLists: { [id: string]: ValuesList } = this._caosDefinitions.values_lists;


        // Create output map
        const valuesListsByKey: { [key: int | string]: string } = {};


        // Create all possible keys to find a command by
        for (const key in valuesLists) {
            const valueList = valuesLists[key];
            valuesListsByKey[key] = key;
            valuesListsByKey[this._crushName(valueList.name)] = key;
            if (isInt(key)) {
                valuesListsByKey[parseInt(key, 10)] = key;
            }
            valuesListsByKey[valueList.id] = key;
        }

        this._valuesListIdsByMixedKeysMap = valuesListsByKey;

        return valuesListsByKey;
    }


    /**
     * Normalizes name for search
     * @param name
     * @private
     */
    private _crushName(name: string): string {
        return name
            .toLowerCase()
            .replace(this._whitespaceRegex, "");
    }
}